import { EMPTY_LIST } from '../../constants/common'
import {
  LOCAL_STORAGE_BIRDVIEW_CHARTS_COLUMN_SPANS_KEY,
  LOCAL_STORAGE_BIRDVIEW_CHARTS_HIDDEN_KEY,
  LOCAL_STORAGE_BIRDVIEW_CHARTS_ORDER_KEY,
  LOCAL_STORAGE_BIRDVIEW_CHARTS_ROW_SPANS_KEY,
  LOCAL_STORAGE_BIRDVIEW_KPIS_ORDER_KEY,
  LOCAL_STORAGE_BIRDVIEW_LAYOUT_KEY,
  LOCAL_STORAGE_BIRDVIEW_TILES_ORDER_KEY,
} from '../../constants/storage'
import {
  ADMIN_USER_ROLE,
  DWELLER_USER_ROLE,
  LANDLORD_USER_ROLE,
  MANAGER_USER_ROLE,
} from '../../constants/user'

import {
  QA_NAME_DASHBOARD_BIRDVIEW_SCOPE_MINE,
  QA_NAME_DASHBOARD_BIRDVIEW_SCOPE_TEAM,
  QA_NAME_DASHBOARD_BIRDVIEW_VIEW_ANALYTICS,
  QA_NAME_DASHBOARD_BIRDVIEW_VIEW_DASHBOARD,
} from './DashboardBirdview.constants.qa'

export const DashboardSeverity = {
  Error: 'error',
  Info: 'info',
  InProgress: 'inProgress',
  Success: 'success',
  Warning: 'warning',
}

export const DashboardTileTone = {
  Error: 'error',
  Info: 'info',
  Purple: 'purple',
  Success: 'success',
  Warning: 'warning',
}

export const BirdviewState = {
  Empty: 'empty',
  Error: 'error',
  Loading: 'loading',
  Normal: 'normal',
}

export const BirdviewSection = {
  Activities: 'activities',
  Attention: 'attention',
  ChartRequests: 'chartRequests',
  Communication: 'communication',
  CriticalRequests: 'criticalRequests',
  DocFlows: 'docFlows',
  DwellerAnnouncements: 'dwellerAnnouncements',
  DwellerDocuments: 'dwellerDocuments',
  DwellerHome: 'dwellerHome',
  DwellerMessages: 'dwellerMessages',
  DwellerObjects: 'dwellerObjects',
  DwellerRequests: 'dwellerRequests',
  Greta: 'greta',
  Kpis: 'kpis',
  Properties: 'properties',
  ServiceRequests: 'serviceRequests',
  TeamLoad: 'teamLoad',
  TileBacklog: 'tileBacklog',
  TileNeedsInput: 'tileNeedsInput',
  TileNoProvider: 'tileNoProvider',
  TileOverdue: 'tileOverdue',
  TileUnassigned: 'tileUnassigned',
  TileUnread: 'tileUnread',
}

// These sections ignore the work scope, so a Mine/Team toggle keeps their
// request key and leaves them loaded instead of refetching the dashboard.
export const BIRDVIEW_SCOPE_INDEPENDENT_SECTIONS = [
  BirdviewSection.Communication,
  BirdviewSection.DocFlows,
  BirdviewSection.Greta,
  BirdviewSection.TeamLoad,
  BirdviewSection.TileUnread,
]

export const BirdviewTileKey = {
  Backlog: 'backlog',
  Input: 'input',
  NoProvider: 'noprovider',
  Overdue: 'overdue',
  Unassigned: 'unassigned',
  Unread: 'unread',
}

export const BirdviewSkeleton = {
  Dweller: 'dweller',
  Operational: 'operational',
  Oversight: 'oversight',
}

export const BirdviewHomeContactType = {
  Email: 'email',
  Phone: 'phone',
}

// The roles listed here are the ones the dashboard has a layout for, and the
// route lets nobody else in.
export const BIRDVIEW_ROLES = [
  { skeleton: BirdviewSkeleton.Oversight, value: ADMIN_USER_ROLE },
  { skeleton: BirdviewSkeleton.Operational, value: MANAGER_USER_ROLE },
  { skeleton: BirdviewSkeleton.Dweller, value: DWELLER_USER_ROLE },
  { skeleton: BirdviewSkeleton.Dweller, value: LANDLORD_USER_ROLE },
]

export const BIRDVIEW_DEFAULT_ROLE = MANAGER_USER_ROLE

export const BirdviewScope = {
  Mine: 'mine',
  Team: 'team',
}

export const BIRDVIEW_SCOPE_OPTIONS = [
  {
    labelKey: 'DashboardBirdview.ScopeMineOptionKey',
    qaName: QA_NAME_DASHBOARD_BIRDVIEW_SCOPE_MINE,
    value: BirdviewScope.Mine,
  },
  {
    labelKey: 'DashboardBirdview.ScopeTeamOptionKey',
    qaName: QA_NAME_DASHBOARD_BIRDVIEW_SCOPE_TEAM,
    value: BirdviewScope.Team,
  },
]

export const BirdviewView = {
  Analytics: 'analytics',
  Dashboard: 'dashboard',
}

export const BIRDVIEW_VIEW_OPTIONS = [
  {
    labelKey: 'DashboardBirdview.ViewDashboardOptionKey',
    qaName: QA_NAME_DASHBOARD_BIRDVIEW_VIEW_DASHBOARD,
    value: BirdviewView.Dashboard,
  },
  {
    labelKey: 'DashboardBirdview.ViewAnalyticsOptionKey',
    qaName: QA_NAME_DASHBOARD_BIRDVIEW_VIEW_ANALYTICS,
    value: BirdviewView.Analytics,
  },
]

export const BIRDVIEW_NEEDS_INPUT_TILE_STUB = { count: 5 }

export const BIRDVIEW_TILE_UNAVAILABLE_COUNT = '–'

export const EMPTY_SECTION = { items: EMPTY_LIST, total: 0 }

export const BirdviewRequestAction = {
  Assign: 'assign',
  Open: 'open',
}

export const BIRDVIEW_REQUEST_ACTION_KEY = {
  [BirdviewRequestAction.Assign]: 'DashboardBirdview.RowActionAssignKey',
  [BirdviewRequestAction.Open]: 'Common.ClickToOpenProfileKey',
}

export const BIRDVIEW_AGE_RANGE_LAST_DAY_OFFSET = 1

// A reset clears only the layout of the current view.
export const BIRDVIEW_ANALYTICS_LAYOUT_KEYS = [
  LOCAL_STORAGE_BIRDVIEW_CHARTS_COLUMN_SPANS_KEY,
  LOCAL_STORAGE_BIRDVIEW_CHARTS_HIDDEN_KEY,
  LOCAL_STORAGE_BIRDVIEW_CHARTS_ORDER_KEY,
  LOCAL_STORAGE_BIRDVIEW_CHARTS_ROW_SPANS_KEY,
  LOCAL_STORAGE_BIRDVIEW_KPIS_ORDER_KEY,
]

export const BIRDVIEW_DASHBOARD_LAYOUT_KEYS = [
  LOCAL_STORAGE_BIRDVIEW_LAYOUT_KEY,
  LOCAL_STORAGE_BIRDVIEW_TILES_ORDER_KEY,
]

export const BIRDVIEW_DRAGGING_ITEM_STYLE = { opacity: 0.4 }

export const BIRDVIEW_STORAGE_KEY_SEPARATOR = ':'

export const BIRDVIEW_STORAGE_ITEM_SET_SEPARATOR = '-'

export const BIRDVIEW_REQUEST_KEY_SEPARATOR = ':'

export const BIRDVIEW_META_SEPARATOR = ' · '

export const BIRDVIEW_LIST_SEPARATOR = ', '

export const BIRDVIEW_PREVIEW_SEPARATOR = ': '
